import { Component } from "react";
import { Box, Button, Typography } from "@mui/material";

// Sad panda SVG — used when error is displayed
const SadPanda = () => (
  <svg
    viewBox="0 0 200 200"
    width="160"
    height="160"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Body */}
    <ellipse cx="100" cy="145" rx="52" ry="42" fill="#f0f0f0" stroke="#222" strokeWidth="2" />
    {/* Left ear patch */}
    <circle cx="52" cy="62" r="22" fill="#222" />
    {/* Right ear patch */}
    <circle cx="148" cy="62" r="22" fill="#222" />
    {/* Head */}
    <circle cx="100" cy="90" r="52" fill="#f0f0f0" stroke="#222" strokeWidth="2" />
    {/* Left eye patch */}
    <ellipse cx="78" cy="85" rx="16" ry="14" fill="#222" />
    {/* Right eye patch */}
    <ellipse cx="122" cy="85" rx="16" ry="14" fill="#222" />
    {/* Left eye white */}
    <circle cx="78" cy="86" r="7" fill="white" />
    {/* Right eye white */}
    <circle cx="122" cy="86" r="7" fill="white" />
    {/* Left pupil — looking down-sad */}
    <circle cx="78" cy="89" r="4" fill="#111" />
    {/* Right pupil — looking down-sad */}
    <circle cx="122" cy="89" r="4" fill="#111" />
    {/* Nose */}
    <ellipse cx="100" cy="102" rx="7" ry="5" fill="#ffb3c1" />
    {/* Sad mouth */}
    <path d="M85 116 Q100 108 115 116" stroke="#555" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    {/* Tear left */}
    <ellipse cx="72" cy="99" rx="3" ry="5" fill="#90caf9" opacity="0.85" />
    {/* Tear right */}
    <ellipse cx="128" cy="99" rx="3" ry="5" fill="#90caf9" opacity="0.85" />
    {/* Left arm */}
    <ellipse cx="55" cy="148" rx="13" ry="28" fill="#222" transform="rotate(-15 55 148)" />
    {/* Right arm */}
    <ellipse cx="145" cy="148" rx="13" ry="28" fill="#222" transform="rotate(15 145 148)" />
    {/* Belly spot */}
    <ellipse cx="100" cy="148" rx="28" ry="22" fill="#e8e8e8" />
  </svg>
);

// Happy panda SVG — shown for a moment after reload button click
const HappyPanda = () => (
  <svg
    viewBox="0 0 200 200"
    width="160"
    height="160"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Body */}
    <ellipse cx="100" cy="145" rx="52" ry="42" fill="#f0f0f0" stroke="#222" strokeWidth="2" />
    {/* Left ear patch */}
    <circle cx="52" cy="62" r="22" fill="#222" />
    {/* Right ear patch */}
    <circle cx="148" cy="62" r="22" fill="#222" />
    {/* Head */}
    <circle cx="100" cy="90" r="52" fill="#f0f0f0" stroke="#222" strokeWidth="2" />
    {/* Left eye patch */}
    <ellipse cx="78" cy="85" rx="16" ry="14" fill="#222" />
    {/* Right eye patch */}
    <ellipse cx="122" cy="85" rx="16" ry="14" fill="#222" />
    {/* Left eye — happy squint */}
    <path d="M68 85 Q78 78 88 85" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* Right eye — happy squint */}
    <path d="M112 85 Q122 78 132 85" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* Nose */}
    <ellipse cx="100" cy="102" rx="7" ry="5" fill="#ffb3c1" />
    {/* Happy mouth */}
    <path d="M85 110 Q100 122 115 110" stroke="#555" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    {/* Rosy cheeks */}
    <circle cx="70" cy="108" r="9" fill="#ffb3c1" opacity="0.45" />
    <circle cx="130" cy="108" r="9" fill="#ffb3c1" opacity="0.45" />
    {/* Left arm — raised */}
    <ellipse cx="52" cy="138" rx="13" ry="28" fill="#222" transform="rotate(-40 52 138)" />
    {/* Right arm — raised */}
    <ellipse cx="148" cy="138" rx="13" ry="28" fill="#222" transform="rotate(40 148 138)" />
    {/* Belly spot */}
    <ellipse cx="100" cy="148" rx="28" ry="22" fill="#e8e8e8" />
  </svg>
);

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }

  handleReset = () => {
    this.setState({ reloading: true });
    setTimeout(() => {
      window.location.reload();
    }, 800);
  };

  render() {
    if (this.state.hasError) {
      const { reloading } = this.state;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
            gap: 1.5,
            p: 4,
            background: "linear-gradient(135deg, #f8f9ff 0%, #eef2ff 100%)",
          }}
        >
          {/* Panda illustration */}
          <Box
            sx={{
              transition: "transform 0.4s cubic-bezier(.34,1.56,.64,1)",
              transform: reloading ? "scale(1.15) rotate(-5deg)" : "scale(1)",
              mb: 1,
              filter: reloading
                ? "drop-shadow(0 8px 24px rgba(99,102,241,0.25))"
                : "drop-shadow(0 4px 12px rgba(0,0,0,0.10))",
            }}
          >
            {reloading ? <HappyPanda /> : <SadPanda />}
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            fontWeight={700}
            color={reloading ? "primary.main" : "text.primary"}
            sx={{
              transition: "color 0.3s",
              letterSpacing: "-0.3px",
            }}
          >
            {reloading ? "Hang tight! 🐼" : "Oops, something broke"}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ maxWidth: 320 }}
          >
            {reloading
              ? "The panda is fixing things for you…"
              : "The panda is just as confused as you are. Let's try reloading."}
          </Typography>

          {/* Reload button */}
          <Button
            variant="contained"
            onClick={this.handleReset}
            disabled={reloading}
            sx={{
              mt: 1.5,
              px: 4,
              py: 1.2,
              borderRadius: "50px",
              fontWeight: 600,
              fontSize: "0.95rem",
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
              background: reloading
                ? undefined
                : "linear-gradient(135deg, #6366f1, #818cf8)",
              transition: "all 0.3s",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5, #6366f1)",
                boxShadow: "0 6px 20px rgba(99,102,241,0.45)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {reloading ? "Reloading…" : "Reload Page"}
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
